import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { CampaignPerformanceResponseDto } from '@/generated/korza-api-client/types.gen';
import { mobileLayer } from '@/lib/mobile-layer';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatCurrency } from '@/components/metrics/formatters';
import { NotificationFunnel } from '@/components/funnel/notification-funnel';
import { Button } from '@/components/ui/button';
import { HelpIconButton } from '@/components/help-panel/help-icon-button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

export interface PushFunnelCardProps {
  tenantSlug: string;
  campaignPerformance: CampaignPerformanceResponseDto | undefined;
  currency: string;
  /** Data sufficiency of the campaigns analytics: off → every number "—". */
  hideValues?: boolean;
}

const DASH = '—';

/**
 * Funnel of the period on the Push tab (FUN-04): the totals of
 * `/metrics/campaigns` (sent, delivered, clicks, paid orders) and, under "No
 * período", Viraram pedido, Receita das campanhas and Receita média por
 * campanha. Without campaigns sent in the period: "Nenhuma campanha enviada
 * no período" and "Criar campanha" (FUN-05).
 */
export function PushFunnelCard({
  tenantSlug,
  campaignPerformance: perf,
  currency,
  hideValues = false,
}: PushFunnelCardProps) {
  const { LL, locale } = useI18nContext();
  const isMobile = useIsMobile();

  const body = () => {
    if (!perf) return <Skeleton className="h-[290px] w-full" />;
    if (perf.sentCampaignsCount === 0) {
      return (
        <div className="flex flex-col items-start gap-3 py-2">
          <p className="text-sm text-muted-foreground">
            {LL.metricsPage.pushTab.funnel.empty()}
          </p>
          <Button asChild className={mobileLayer.touch}>
            <Link to={`/${tenantSlug}/campaigns/create`}>
              {LL.metricsPage.pushTab.funnel.create()}
            </Link>
          </Button>
        </div>
      );
    }
    const converted =
      hideValues || perf.totalNotificationsSent === 0
        ? DASH
        : new Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }).format(perf.totalOrderPaidCount / perf.totalNotificationsSent);
    const money = (value: number) =>
      hideValues ? DASH : formatCurrency(value, locale, currency);
    return (
      <NotificationFunnel
        compact={isMobile}
        hideValues={hideValues}
        stages={{
          sent: perf.totalNotificationsSent,
          delivered: perf.totalNotificationsDelivered,
          clicks: perf.totalClicks,
          orders: perf.totalOrderPaidCount,
        }}
        statsTitle={LL.metricsPage.pushTab.funnel.statsTitle()}
        stats={[
          {
            label: LL.metricsPage.pushTab.funnel.converted(),
            hint: LL.metricsPage.pushTab.funnel.convertedHint(),
            value: converted,
          },
          {
            label: LL.metricsPage.pushTab.funnel.revenue(),
            hint: LL.metricsPage.pushTab.funnel.revenueHint(),
            value: money(perf.totalRevenue),
          },
          {
            label: LL.metricsPage.pushTab.funnel.averageRevenue(),
            hint: LL.metricsPage.pushTab.funnel.averageRevenueHint({
              count: perf.sentCampaignsCount,
            }),
            value: money(perf.averageRevenue),
          },
        ]}
      />
    );
  };

  return (
    <Card
      role="region"
      aria-label={LL.metricsPage.pushTab.funnel.title()}
      data-push-funnel
      className="flex flex-col gap-4 p-5 max-md:p-4"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold">
            {LL.metricsPage.pushTab.funnel.title()}
          </h3>
          <HelpIconButton helpKey="pushFunnel" />
        </div>
        <p className="text-[13px] text-muted-foreground">
          {LL.metricsPage.pushTab.funnel.description()}
        </p>
      </div>
      {body()}
    </Card>
  );
}
